import React, { useState } from "react";
import { Row, Col, Card, Typography, Button, Switch } from "antd";
import {
  CheckOutlined,
  StarFilled,
  ArrowRightOutlined,
} from "@ant-design/icons";

const { Title, Paragraph } = Typography;

const PRICING_PLANS = [
  {
    id: "starter",
    name: "Starter Plan",
    idealFor: "Small sales teams (1–5 Reps)",
    monthlyPrice: 999,
    annualPrice: 799,
    btnText: "Start Free Trial",
    isPrimary: false,
    isPopular: false,
    tagline: "Essential Android telephony sync & pipeline management for emerging sales squads.",
    highlights: [
      "Android Call Sync & Native Mobile App",
      "Pipeline Kanban & Visual Deal Flow",
      "> 7 Days Stagnant Deal Warning Radar",
      "Today's Action Queue & Callback Tasks",
      "Basic Customer Profiles & Invoicing",
      "Standard Email Support",
    ],
  },
  {
    id: "professional",
    name: "Professional Plan",
    idealFor: "High-growth teams (5–25 Reps)",
    monthlyPrice: 1999,
    annualPrice: 1599,
    btnText: "Start 14-Day Free Trial",
    isPrimary: true,
    isPopular: true,
    tagline: "Complete sales management suite with hourly telemetry, leaderboards & GST quotations.",
    highlights: [
      "Everything in Starter, plus:",
      "Admin Hourly Call Telemetry (9AM–7PM)",
      "Real-Time Rep Performance Leaderboards",
      "Customer 360° Vault (ROC, DIN, SPOC & KYC)",
      "Versioned GST Quotes + Razorpay Links",
      "Live WebSocket Presence & Break Tracking",
      "Priority Support (4-Hour SLA)",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise Plan",
    idealFor: "Large sales forces (25+ Reps)",
    monthlyPrice: "Custom",
    annualPrice: "Custom",
    btnText: "Contact Sales",
    isPrimary: false,
    isPopular: false,
    tagline: "Dedicated database sharding, custom payment gateways, and custom SLA for scaling enterprises.",
    highlights: [
      "Everything in Professional, plus:",
      "Multi-Department Custom Rankings",
      "Custom Letterheads & Payment Gateways",
      "Advanced Overtime Compliance Engine",
      "Dedicated Database Instance / Shard",
      "24/7 Dedicated Account Manager",
    ],
  },
];

function PricingSection({ openSignup, openTrialSignup }) {
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <section className="pricing" id="pricing">
      <div className="section-container">
        {/* Section Header */}
        <div className="pricing-header-center">
          <span className="section-eyebrow-badge">TRANSPARENT &amp; PREDICTABLE</span>
          <Title level={2} className="pricing-main-heading">
            Transparent B2B pricing{" "}
            <span className="text-highlight-blue">built to scale with your team</span>
          </Title>
          <Paragraph className="pricing-main-subheading">
            Simple, predictable plans designed for high-velocity sales teams. No hidden platform fees or multi-year lock-ins.
          </Paragraph>

          {/* Billing Switcher */}
          <div className="billing-toggle-bar">
            <span className={`toggle-label ${!isAnnual ? "label-active" : ""}`}>
              Monthly Billing
            </span>
            <Switch
              checked={isAnnual}
              onChange={(checked) => setIsAnnual(checked)}
              className="billing-switch-btn"
            />
            <span className={`toggle-label ${isAnnual ? "label-active" : ""}`}>
              Annual Billing
            </span>
            <span className="save-badge">SAVE 20%</span>
          </div>
        </div>

        {/* 3 Plan Cards Grid */}
        <Row gutter={[24, 24]} className="pricing-cards-row" align="stretch">
          {PRICING_PLANS.map((plan) => {
            const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
            return (
              <Col xs={24} lg={8} key={plan.id}>
                <Card
                  className={`pricing-card-b2b ${plan.isPopular ? "card-popular" : ""}`}
                  bordered={false}
                >
                  {plan.isPopular && (
                    <div className="b2b-popular-ribbon">
                      <StarFilled style={{ fontSize: "11px" }} />
                      <span>MOST POPULAR</span>
                    </div>
                  )}

                  <div className="b2b-card-top">
                    <Title level={3} className="b2b-plan-title">
                      {plan.name}
                    </Title>
                    <div className="b2b-ideal-badge">{plan.idealFor}</div>
                    <Paragraph className="b2b-plan-tagline">{plan.tagline}</Paragraph>
                  </div>

                  <div className="b2b-card-pricing">
                    {typeof price === "number" ? (
                      <div className="price-display">
                        <span className="currency-symbol">₹</span>
                        <span className="amount-number">
                          {price.toLocaleString("en-IN")}
                        </span>
                        <span className="period-label">/ user / month</span>
                      </div>
                    ) : (
                      <div className="price-display">
                        <span className="amount-custom">Custom Pricing</span>
                      </div>
                    )}
                    <span className="billing-subtext">
                      {typeof price === "number"
                        ? isAnnual
                          ? "Billed annually (2 months free included)"
                          : "Billed on a flexible monthly basis"
                        : "Tailored agreement for large teams"}
                    </span>
                  </div>

                  <Button
                    type={plan.isPrimary ? "primary" : "default"}
                    size="large"
                    block
                    className={`b2b-cta-btn ${plan.isPrimary ? "btn-highlight" : ""}`}
                    onClick={() => {
                      if (plan.id === "enterprise") {
                        if (typeof openTrialSignup === "function") openTrialSignup("Enterprise Plan Request");
                      } else if (plan.id === "starter") {
                        if (typeof openSignup === "function") openSignup(false);
                      } else {
                        if (typeof openTrialSignup === "function") openTrialSignup("");
                      }
                    }}
                  >
                    {plan.btnText}
                  </Button>

                  <div className="b2b-card-features">
                    <div className="features-header-label">PLAN CAPABILITIES</div>
                    <div className="features-list">
                      {plan.highlights.map((item, hIdx) => (
                        <div className="feature-bullet-row" key={hIdx}>
                          <CheckOutlined
                            style={{
                              color: plan.isPopular ? "#2563eb" : "#10b981",
                              fontSize: "13px",
                              marginTop: "3px",
                              fontWeight: "bold",
                            }}
                          />
                          <span className="feature-bullet-text">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    </section>
  );
}

export default PricingSection;
